// Minimal GA4 (gtag.js) integration for a React SPA.
// Initialization is idempotent (safe to call more than once, e.g. under
// React StrictMode's double-invoked effects) and never throws — if the
// measurement ID is missing or the script fails to load (ad blockers,
// offline, etc.), the app continues to work with analytics simply absent.

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

let initialized = false;
let lastTrackedPage = null;

export function initGA() {
  if (initialized) return;
  if (typeof window === "undefined") return;
  if (!MEASUREMENT_ID) {
    console.warn("Google Analytics: VITE_GA_MEASUREMENT_ID is not set, skipping init.");
    return;
  }

  try {
    initialized = true;

    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = window.gtag || gtag;

    window.gtag("js", new Date());
    // send_page_view is disabled here — page views are sent manually on every
    // route change (including the first one) so there is exactly one
    // page_view per navigation, never a duplicate from gtag's own default.
    window.gtag("config", MEASUREMENT_ID, { send_page_view: false });

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    script.onerror = () => {
      console.warn("Google Analytics: gtag.js failed to load; continuing without analytics.");
    };
    document.head.appendChild(script);
  } catch (err) {
    console.warn("Google Analytics: initialization failed, continuing without analytics.", err);
  }
}

export function trackPageView(path, title) {
  if (!MEASUREMENT_ID) return;
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  if (path === lastTrackedPage) return; // guard against duplicate consecutive sends

  try {
    lastTrackedPage = path;
    window.gtag("event", "page_view", {
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.href,
    });
  } catch (err) {
    console.warn("Google Analytics: failed to send page_view.", err);
  }
}
